import React from 'react';

interface SkeletonLoaderProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  lines?: number;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  className = '',
  variant = 'text',
  width,
  height,
  lines = 1,
}) => {
  const baseClasses = 'animate-pulse bg-gray-200';
  
  const variantClasses = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  if (variant === 'text' && lines > 1) {
    return (
      <div className={className}>
        {Array.from({ length: lines }, (_, index) => (
          <div
            key={index}
            className={`${baseClasses} ${variantClasses[variant]} h-4 mb-2 ${
              index === lines - 1 ? 'w-3/4' : 'w-full'
            }`}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={style}
    />
  );
};

// 预定义的骨架屏组件
export const StatCardSkeleton: React.FC = () => (
  <div className="text-center animate-pulse">
    <SkeletonLoader variant="circular" width={64} height={64} className="mx-auto mb-4" />
    <SkeletonLoader variant="text" height={32} className="mb-2" />
    <SkeletonLoader variant="text" height={16} width={64} className="mx-auto" />
  </div>
);

export const FeatureCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
    <SkeletonLoader variant="rectangular" width={48} height={48} className="mb-4" />
    <SkeletonLoader variant="text" height={24} className="mb-2" />
    <SkeletonLoader variant="text" lines={2} className="mb-4" />
    <SkeletonLoader variant="text" height={16} width={80} />
  </div>
);

export const TestimonialSkeleton: React.FC = () => (
  <div className="bg-white rounded-2xl p-8 shadow-lg animate-pulse">
    <SkeletonLoader variant="circular" width={32} height={32} className="mx-auto mb-6" />
    <SkeletonLoader variant="text" lines={3} className="mb-6" />
    <div className="flex justify-center mb-4">
      {Array.from({ length: 5 }, (_, i) => (
        <SkeletonLoader key={i} variant="circular" width={16} height={16} className="mx-1" />
      ))}
    </div>
    <div className="flex items-center justify-center">
      <SkeletonLoader variant="circular" width={48} height={48} className="mr-4" />
      <div>
        <SkeletonLoader variant="text" height={20} width={100} className="mb-1" />
        <SkeletonLoader variant="text" height={16} width={80} />
      </div>
    </div>
  </div>
);

export default SkeletonLoader;